import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { matchesAll } from "@/lib/trFold";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { FileText, Loader2, Search, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = { kumas: "Kumaş", aksesuar: "Aksesuar", hazir_urun: "Hazır Ürün" };
const KIND_TONE = {
  kumas: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  aksesuar: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  hazir_urun: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};
const CUR_SYMBOL = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });

export default function QuotesBrowserDialog({ open, onOpenChange, onPick }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | converted

  useEffect(() => {
    if (!open) return;
    setQuery(""); setStatusFilter("all");
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/purchase-quotes");
        setQuotes(data?.quotes || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [open]);

  // material_id+kind+variant_id bazlı grupla
  const groups = useMemo(() => {
    const map = new Map();
    for (const qt of quotes) {
      const key = `${qt.material_id}|${qt.kind}|${qt.variant_id || ""}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(qt);
    }
    let out = [...map.entries()].map(([gkey, list]) => {
      const first = list[0] || {};
      const active = list.filter((x) => x.status === "active");
      const best = active.length ? Math.min(...active.map((x) => Number(x.unit_price || Infinity))) : null;
      const latest = list[0]?.created_at || "";
      return {
        key: gkey,
        material_id: first.material_id,
        kind: first.kind,
        variant_id: first.variant_id || "",
        name: first.material_name || first.material_id,
        count: list.length,
        activeCount: active.length,
        convertedCount: list.length - active.length,
        bestPrice: best,
        currency: active[0]?.currency || "TRY",
        suppliers: [...new Set(list.map((x) => x.supplier_name).filter(Boolean))],
        latest,
      };
    });
    if (statusFilter === "active") out = out.filter((g) => g.activeCount > 0);
    if (statusFilter === "converted") out = out.filter((g) => g.convertedCount > 0);
    if (query.trim()) {
      out = out.filter((g) => matchesAll(query, [g.name, ...g.suppliers]));
    }
    return out;
  }, [quotes, query, statusFilter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-2xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="qb-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-slate-100 flex items-center gap-2">
            <FileText className="w-4 h-4 text-sky-400" /> Tedarikçi Teklifleri
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Toplanmış tüm teklifler. Satıra tıklayarak karşılaştırma ekranını tekrar açabilirsiniz.
          </DialogDescription>
        </DialogHeader>

        <div className="shrink-0 flex items-center gap-2 pb-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="qb-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Ürün veya tedarikçi ara…" className="pl-9 bg-slate-950 border-slate-700 rounded-sm h-9" />
          </div>
          <div className="flex gap-1" data-testid="qb-status-filter">
            {[["all", "Tümü"], ["active", "Aktif"], ["converted", "Satın Almaya Dönüştü"]].map(([v, l]) => (
              <Button key={v} size="sm" variant="ghost" data-testid={`qb-filter-${v}`}
                onClick={() => setStatusFilter(v)}
                className={`h-8 text-[11px] rounded-sm ${statusFilter === v ? "bg-slate-800 text-slate-100" : "text-slate-500"}`}>
                {l}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 pr-1" data-testid="qb-list">
          {loading ? (
            <div className="flex justify-center py-14"><Loader2 className="w-5 h-5 text-sky-500 animate-spin" /></div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">Kayıtlı teklif bulunamadı.</div>
          ) : groups.map((g) => (
            <button key={g.key} type="button" data-testid={`qb-group-${g.material_id}`}
              onClick={() => { onOpenChange(false); onPick?.(g); }}
              className="w-full text-left rounded-sm border border-slate-800 bg-slate-950/60 px-3.5 py-2.5 hover:border-sky-700/60 hover:bg-slate-900 transition-colors flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-100 font-medium truncate">{g.name}</span>
                  <Badge className={`text-[9px] border shrink-0 ${KIND_TONE[g.kind] || ""}`}>{KIND_LABEL[g.kind] || g.kind}</Badge>
                </div>
                <div className="text-[10px] font-mono text-slate-500 mt-0.5 truncate">
                  {g.count} teklif · {g.suppliers.slice(0, 3).join(", ")}{g.suppliers.length > 3 ? "…" : ""}
                  {g.convertedCount > 0 ? ` · ${g.convertedCount} satın alma siparişine dönüştü` : ""}
                </div>
              </div>
              <div className="text-right shrink-0">
                {g.bestPrice != null && (
                  <div className="font-mono text-xs text-sky-300 tabular-nums">{CUR_SYMBOL[g.currency] || ""}{fmt(g.bestPrice)}</div>
                )}
                <div className="text-[9px] font-mono text-slate-600">{g.latest ? new Date(g.latest).toLocaleDateString("tr-TR") : ""}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
